import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DismissalStore,
  storageKeyFor,
  DISMISSALS_CHANGED_EVENT,
  type DismissOptions,
} from '../services/DismissalStore'

type UseDismissalOptions = {
  scope?: string
}

type UseDismissalReturn = {
  dismissed: boolean
  dismiss: (opts?: DismissOptions) => void
  reset: () => void
  expiresAt?: number
}

export const useDismissal = (id: string, options?: UseDismissalOptions): UseDismissalReturn => {
  const scope = options?.scope ?? 'global'
  const key = useMemo(() => storageKeyFor(id, scope), [id, scope])

  const read = useCallback(() => {
    const rec = DismissalStore.get(id, scope)
    return {
      dismissed: !!rec,
      expiresAt: rec?.expiresAt,
    }
  }, [id, scope])

  const [{ dismissed, expiresAt }, setState] = useState(read)

  useEffect(() => {
    setState(read())
    const handler = (e: StorageEvent) => {
      if (e.key === key) setState(read())
    }
    window.addEventListener('storage', handler)
    // Also update within the same window when the store broadcasts changes
    const inWindowHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { key?: string } | undefined
      if (!detail || !detail.key || detail.key === key) setState(read())
    }
    window.addEventListener(DISMISSALS_CHANGED_EVENT, inWindowHandler)
    return () => {
      window.removeEventListener('storage', handler)
      window.removeEventListener(DISMISSALS_CHANGED_EVENT, inWindowHandler)
    }
  }, [key, read])

  const dismiss = useCallback(
    (opts?: DismissOptions) => {
      const rec = DismissalStore.dismiss(id, opts, scope)
      setState({ dismissed: true, expiresAt: rec.expiresAt })
    },
    [id, scope]
  )

  const reset = useCallback(() => {
    DismissalStore.reset(id, scope)
    setState({ dismissed: false, expiresAt: undefined })
  }, [id, scope])

  return { dismissed, dismiss, reset, expiresAt }
}

export default useDismissal
